import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, ListChecks } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useSmartCTA } from "@/hooks/useSmartCTA";

/*
 * Standalone visual guide for clients: how to post a job in 8 steps, each with
 * a small inline SVG illustration. Linked from the FAQ (faq.items.q5) and
 * reachable at /guides/post-a-job. Copy lives in i18n (guides.postJob.*); the
 * final CTA shares the Hero's primary destination (cta.postJob()).
 *
 * SVGs use the brand palette directly: hive #F5B800 / deep #D99500, worker
 * #1A1614, cream #FAF6EE, linen #F2EBDC, sand #E5DCC4, smoke #5C4B38.
 */

const svgProps = { viewBox: "0 0 160 110", className: "w-full h-auto", role: "presentation" };

function LoginSvg() {
  return (
    <svg {...svgProps}>
      <rect x="34" y="16" width="92" height="78" rx="9" fill="#FAF6EE" stroke="#E5DCC4" strokeWidth="2" />
      <circle cx="80" cy="30" r="8" fill="#F5B800" stroke="#1A1614" strokeWidth="1.5" />
      <rect x="48" y="46" width="64" height="12" rx="3" fill="#fff" stroke="#5C4B38" strokeWidth="1.5" />
      <rect x="48" y="64" width="64" height="12" rx="3" fill="#fff" stroke="#5C4B38" strokeWidth="1.5" />
      <rect x="48" y="82" width="64" height="14" rx="4" fill="#F5B800" />
    </svg>
  );
}

function TapButtonSvg() {
  return (
    <svg {...svgProps}>
      <circle cx="102" cy="58" r="20" fill="none" stroke="#D99500" strokeWidth="2" opacity="0.45" />
      <rect x="26" y="40" width="92" height="30" rx="15" fill="#F5B800" />
      <rect x="42" y="52" width="60" height="7" rx="3.5" fill="#1A1614" />
      <path d="M100 60 l0 30 l8 -8 l6 12 l7 -3 l-6 -12 l11 0 z" fill="#1A1614" stroke="#FAF6EE" strokeWidth="1.6" />
    </svg>
  );
}

function DescribeSvg() {
  return (
    <svg {...svgProps}>
      <rect x="30" y="20" width="100" height="70" rx="9" fill="#FAF6EE" stroke="#E5DCC4" strokeWidth="2" />
      <rect x="42" y="34" width="78" height="7" rx="3.5" fill="#5C4B38" />
      <rect x="42" y="47" width="66" height="7" rx="3.5" fill="#5C4B38" opacity="0.6" />
      <rect x="42" y="60" width="72" height="7" rx="3.5" fill="#5C4B38" opacity="0.6" />
      <path d="M118 58 l3.4 9 l9 3.4 l-9 3.4 l-3.4 9 l-3.4 -9 l-9 -3.4 l9 -3.4 z" fill="#F5B800" stroke="#D99500" strokeWidth="1" />
    </svg>
  );
}

function PhotosSvg() {
  return (
    <svg {...svgProps}>
      <rect x="36" y="22" width="40" height="30" rx="4" fill="#F2EBDC" stroke="#E5DCC4" strokeWidth="2" />
      <rect x="84" y="22" width="40" height="30" rx="4" fill="#F2EBDC" stroke="#E5DCC4" strokeWidth="2" />
      <rect x="36" y="58" width="40" height="30" rx="4" fill="#F2EBDC" stroke="#E5DCC4" strokeWidth="2" />
      <rect x="84" y="58" width="40" height="30" rx="4" fill="#F2EBDC" stroke="#E5DCC4" strokeWidth="2" />
      <circle cx="52" cy="35" r="4" fill="#F5B800" />
      <path d="M40 48 l9 -9 l7 7 l6 -5 l10 11 z" fill="#5C4B38" opacity="0.5" />
      <rect x="68" y="70" width="28" height="20" rx="5" fill="#1A1614" />
      <circle cx="82" cy="80" r="6" fill="#F5B800" stroke="#FAF6EE" strokeWidth="1.5" />
    </svg>
  );
}

function LocationSvg() {
  return (
    <svg {...svgProps}>
      <ellipse cx="80" cy="90" rx="44" ry="9" fill="#F2EBDC" />
      <path d="M80 22 C66 22 55 33 55 47 C55 65 80 88 80 88 C80 88 105 65 105 47 C105 33 94 22 80 22 Z" fill="#F5B800" stroke="#1A1614" strokeWidth="2" />
      <circle cx="80" cy="46" r="9" fill="#FAF6EE" stroke="#1A1614" strokeWidth="2" />
    </svg>
  );
}

function BudgetDatesSvg() {
  return (
    <svg {...svgProps}>
      <rect x="24" y="42" width="48" height="32" rx="6" fill="#F5B800" stroke="#1A1614" strokeWidth="2" />
      <circle cx="35" cy="53" r="3.5" fill="#1A1614" />
      <text x="52" y="65" fontSize="20" fontWeight="700" fill="#1A1614" textAnchor="middle">$</text>
      <rect x="86" y="36" width="46" height="42" rx="6" fill="#FAF6EE" stroke="#1A1614" strokeWidth="2" />
      <rect x="86" y="36" width="46" height="13" rx="6" fill="#1A1614" />
      <line x1="97" y1="31" x2="97" y2="43" stroke="#1A1614" strokeWidth="3" strokeLinecap="round" />
      <line x1="121" y1="31" x2="121" y2="43" stroke="#1A1614" strokeWidth="3" strokeLinecap="round" />
      <circle cx="98" cy="60" r="2.6" fill="#5C4B38" />
      <circle cx="109" cy="60" r="2.6" fill="#5C4B38" />
      <circle cx="120" cy="60" r="2.6" fill="#F5B800" stroke="#D99500" strokeWidth="1" />
      <circle cx="98" cy="70" r="2.6" fill="#5C4B38" />
      <circle cx="109" cy="70" r="2.6" fill="#5C4B38" />
    </svg>
  );
}

function NotifySvg() {
  return (
    <svg {...svgProps}>
      <rect x="26" y="58" width="24" height="30" rx="3" fill="#FAF6EE" stroke="#5C4B38" strokeWidth="1.5" />
      <rect x="110" y="58" width="24" height="30" rx="3" fill="#FAF6EE" stroke="#5C4B38" strokeWidth="1.5" />
      <path d="M80 28 a13 13 0 0 1 13 13 c0 15 6 19 6 19 l-38 0 s6 -4 6 -19 a13 13 0 0 1 13 -13 z" fill="#F5B800" stroke="#1A1614" strokeWidth="2" />
      <circle cx="80" cy="26" r="3.5" fill="#1A1614" />
      <path d="M73 66 a7 7 0 0 0 14 0" fill="none" stroke="#1A1614" strokeWidth="2" strokeLinecap="round" />
      <circle cx="98" cy="34" r="9" fill="#B33A2E" stroke="#FAF6EE" strokeWidth="1.5" />
      <text x="98" y="38" fontSize="11" fontWeight="700" fill="#fff" textAnchor="middle">3</text>
    </svg>
  );
}

function CompareSvg() {
  return (
    <svg {...svgProps}>
      <rect x="22" y="30" width="48" height="56" rx="6" fill="#FAF6EE" stroke="#E5DCC4" strokeWidth="2" />
      <rect x="30" y="42" width="32" height="6" rx="3" fill="#5C4B38" opacity="0.6" />
      <rect x="30" y="54" width="26" height="6" rx="3" fill="#5C4B38" opacity="0.6" />
      <rect x="30" y="66" width="30" height="6" rx="3" fill="#5C4B38" opacity="0.6" />
      <rect x="74" y="30" width="48" height="56" rx="6" fill="#FAF6EE" stroke="#F5B800" strokeWidth="2.5" />
      <rect x="82" y="42" width="32" height="6" rx="3" fill="#5C4B38" opacity="0.6" />
      <rect x="82" y="54" width="26" height="6" rx="3" fill="#5C4B38" opacity="0.6" />
      <rect x="82" y="66" width="30" height="6" rx="3" fill="#5C4B38" opacity="0.6" />
      <circle cx="116" cy="34" r="9" fill="#4A7C3A" stroke="#FAF6EE" strokeWidth="1.5" />
      <path d="M111 34 l3.4 3.4 l5.6 -6.4" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M100 72 h30 a4 4 0 0 1 4 4 v13 a4 4 0 0 1 -4 4 h-15 l-8 6 v-6 h-7 a4 4 0 0 1 -4 -4 v-13 a4 4 0 0 1 4 -4 z" fill="#F5B800" stroke="#1A1614" strokeWidth="1.5" />
    </svg>
  );
}

const STEPS = [
  { n: 1, Svg: LoginSvg },
  { n: 2, Svg: TapButtonSvg },
  { n: 3, Svg: DescribeSvg },
  { n: 4, Svg: PhotosSvg },
  { n: 5, Svg: LocationSvg },
  { n: 6, Svg: BudgetDatesSvg },
  { n: 7, Svg: NotifySvg },
  { n: 8, Svg: CompareSvg },
];

export default function PostJobGuide() {
  const { t } = useTranslation();
  const cta = useSmartCTA();
  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="post-job-guide">
      <Navbar />
      <main className="section flex-1">
        <div className="container-app max-w-3xl">
          <Link to="/" className="fx-btn fx-btn--ghost text-sm mb-4 inline-flex" data-testid="back-home">
            <ArrowLeft size={14} /> {t("common.backHome")}
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="text-hive-ink" />
            <h1 className="font-display text-4xl md:text-5xl font-extrabold text-worker">{t("guides.postJob.title")}</h1>
          </div>
          <p className="text-smoke text-lg max-w-xl">{t("guides.postJob.subtitle")}</p>

          <ol className="mt-8 space-y-5">
            {STEPS.map(({ n, Svg }) => (
              <li key={n} className="card-base p-5 sm:flex sm:items-center gap-6" data-testid={`post-job-step-${n}`}>
                <div className="sm:w-44 shrink-0 mb-4 sm:mb-0">
                  <div className="rounded-md bg-linen border border-sand p-3">
                    <Svg />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-hive text-worker font-display font-bold text-sm flex items-center justify-center shrink-0">
                      {n}
                    </span>
                    <h3 className="font-display font-bold text-worker text-lg leading-tight">
                      {t(`guides.postJob.steps.s${n}.title`)}
                    </h3>
                  </div>
                  <p className="mt-2 text-smoke leading-relaxed">{t(`guides.postJob.steps.s${n}.body`)}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-10 text-center">
            <Link to={cta.postJob()} className="fx-btn fx-btn--primary text-base" data-testid="post-job-guide-cta">
              {t("guides.postJob.cta")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
